import React, { useState } from "react";
import Breadcrumb from "../../../Components/Common/Breadcrumb";
import useSWR from "swr";
import { CategoryApiUrls, orderUrls } from "../../../Api/Auth/ApiUrls";
import QueryString from "qs";
import useTable from "../../../hooks/useTable";
import Table from "../../../Components/Common/Table";
import TableHead from "../../../Components/Common/TableHead";
import DashboardRow from "./Components/DashboardTableRow";
import { Loader } from "../../../Components/Common/Loader";
import NoData from "../../../Components/Common/NoData";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../Context/AuthContext";
import NotFound from "../../Notfound";
import useSWRMutation from "swr/mutation";
import { sendPutRequestAuth } from "../../../Api/swrConfig";
import { toast } from "react-toastify";
import useToastError from "../../../hooks/useErrorToast";
import Modal from "../../../Components/Common/Modal";
import Input from "../../../Components/Common/Input";

const tableColumnData = [
  { id: "_id", label: "Order ID" },
  { id: "item", label: "Item" },
  { id: "userName", label: "Ordered By" },
  { id: "amount", label: "Amount" },
  { id: "transactionId", label: "Transaction ID" },
  { id: "createdAt", label: "Created" },
];

const AdminDashboard = () => {
  const location = useLocation();
  const { showErrorToast } = useToastError();
  const { userData } = useAuth();
  const { categoryMutate: globalMutate } = useAuth();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTable({ defaultOrderBy: "" });
  const apiQueryParams = {
    offset: (page - 1) * rowsPerPage,
    limit: rowsPerPage,
  };
  const apiQueryString = QueryString.stringify(apiQueryParams);
  const [mutatingId, setMutatingId] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [category, setCategory] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    visibleTitle: "",
  });

  const { data: categoryData, mutate: categoryMutate } = useSWR(
    CategoryApiUrls.list
  );

  const onChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // Set only the field that's changed
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.visibleTitle) newErrors.visibleTitle = "Title is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    const modifiedData = {
      visibleTitle: formData.visibleTitle,
      id: category._id,
    };
    await EditTrigger(modifiedData);
  };

  const { trigger: EditTrigger } = useSWRMutation(
    CategoryApiUrls.list,
    async (url, { arg }) => {
      const response = await sendPutRequestAuth(
        CategoryApiUrls.edit(arg.id),
        arg.visibleTitle
          ? {
              arg: { visibleTitle: arg.visibleTitle },
            }
          : {
              arg: { visibility: arg.visibility },
            }
      );
      return response;
    },
    {
      onSuccess: (data) => {
        toast.success("Success");
        setMutatingId(null);
        setModalOpen(false);
        setFormData({ visibleTitle: "" });
        globalMutate();
      },
      onError: (error) => {
        showErrorToast(error);
        setMutatingId(null);
      },
    }
  );

  const handleToggle = async (item) => {
    setMutatingId(item._id);

    // Optimistic update - update UI immediately
    categoryMutate(
      (currentData) => {
        if (!currentData) return currentData;
        return {
          ...currentData,
          data: currentData.data.map((cat) =>
            cat._id === item._id ? { ...cat, visibility: !cat.visibility } : cat
          ),
        };
      },
      false // Don't revalidate immediately
    );

    // Then trigger the API call
    try {
      await EditTrigger({ id: item._id, visibility: !item.visibility });
      // Revalidate after success to ensure data is in sync
      categoryMutate();
    } catch (error) {
      // Error handling is in onError callback
    }
  };

  const { isLoading, data, mutate } = useSWR(orderUrls.list(apiQueryString));

  if (userData?.roleId === 6) {
    return <NotFound />;
  }

  return (
    <div>
      <Breadcrumb actionButtons={[]} />
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-xl font-semibold text-gray-800">All Orders</h3>

        <div className="flex flex-col items-end gap-1">
          <span className="text-sm text-gray-600 font-medium mb-2">
            All Courses
          </span>
          <div className="flex gap-4">
            {categoryData?.data?.map((item, index) => (
              <div key={index} className="flex items-center gap-2.5">
                <span className="text-sm font-medium text-gray-700">
                  {item.visibleTitle}
                </span>

                <div className="flex items-center gap-2">
                  {/* Edit Button */}
                  <button
                    onClick={() => {
                      // Add your edit handler here
                      setCategory(item);
                      setModalOpen(true);
                      onChange("visibleTitle", item.visibleTitle);
                    }}
                    className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                    aria-label={`Edit ${item.title}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </button>

                  {/* Vertical Separator */}

                  {/* Toggle Switch */}
                  <button
                    onClick={() => {
                      handleToggle(item);
                    }}
                    disabled={mutatingId === item._id}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ${
                      mutatingId === item._id
                        ? "bg-gray-400 cursor-wait"
                        : item.visibility
                        ? "bg-green-500"
                        : "bg-gray-300"
                    }`}
                    aria-label={`Toggle ${item.title}`}
                  >
                    {mutatingId === item._id ? (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <svg
                          className="animate-spin h-3 w-3 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </span>
                    ) : (
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                          item.visibility ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    )}
                  </button>
                  {index === 0 && <div className="h-6 w-px bg-gray-400"></div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Table>
        <TableHead rowCount={data?.data.length} headerData={tableColumnData} />
        <tbody className={`divide-y divide-gray-200`}>
          {!isLoading ? (
            <>
              {data?.data.map((row) => (
                <DashboardRow key={row._id} mutate={mutate} row={row} />
              ))}
            </>
          ) : (
            <tr>
              <td colSpan="100" className="text-center py-4">
                <Loader />
              </td>
            </tr>
          )}
        </tbody>
        <NoData isNotFound={!data?.data.length && !isLoading} />
      </Table>
      <Pagination
        totalRows={data?.pagination.totalItems || 0}
        currentPage={page}
        rowsPerPage={rowsPerPage}
        currentPath={location.pathname}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Modal
        isOpen={isModalOpen}
        title={"Edit Course Name"}
        onConfirm={handleSubmit}
        onCancel={() => {
          setErrors({});
          setModalOpen(false);
        }}
        // isLoading={isMutating || editMutating}
        formWidth
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <Input
            label="Title"
            type="text"
            name="visibleTitle"
            value={formData.visibleTitle}
            onChange={onChange}
            placeholder="Enter course title"
            error={errors.visibleTitle}
            required
          />
        </form>
      </Modal>
    </div>
  );
};

export default AdminDashboard;
